import React, { useCallback, useEffect, useState } from 'react';
import { styled } from '@nykaa/ui-components';
import { loadVerloop, trackUser } from 'store/auth/helper';
import setUpSessionCount from 'helpers/utils/sessionCount';
import { addAutoSignInEvent } from 'shared/store/smartLock/autoSignin';
import { storeCredentials } from 'store/smartLock/helper';
import { RequestConfig } from '@nykaa/utils/network';
import { handleStoreId } from 'helpers/store';
import { handleDevice } from 'helpers/deviceIdentity';
import {
  getAuthSteps,
  deleteAuthSteps,
  getMobileNumberLocal,
  setLoginLocation,
} from 'shared/helpers/auth';
import { LOGIN_LOCATION, MOBILE_VERIFICATION_URL } from 'shared/constants/auth';
import { pushMobileVerificationEvents } from 'shared/User/helpers/mobileVerification';
import trackingEvent, {
  LOGIN_CODES,
} from 'shared/User/constants/mobileVerification/trackingEvents';
import { pushData } from '@nykaa/data-layer/utils';
import { useDispatch, useSelector } from 'react-redux';
import appEvents from 'store/app/actionTypes';
import { State } from 'store/types';
import { deleteCookie, setCookie } from '@nykaa/utils/cookies';
import { PRIVE_TIER_KEY } from '@nykaa/auth-data-handler/store/constants';
// import { useIsTagsV2Enabled } from 'hooks/Product/tags';

const FrameWrapper = styled.div`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.textDisabled};
  transform: translate(-50%, -50%);
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 11;
  border: none;
`;

const Iframe = styled.iframe`
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  height: auto;
  overflow: hidden;
  max-width: 500px;
  width: 492px;
  min-height: 400px;
  position: fixed;
  border: none;
  border-radius: ${({ theme }) => theme.borders.radius30};
`;

interface BootProps {
  configFlagsFetched: boolean;
  fetchAuth: () => Promise<void>;
  email: string;
  firstName: string;
  formKey: string;
  id: string;
  dataFetched: boolean;
  fetchedOverNetwork: boolean;
  userName: string;
  storeId: string;
  isPro: boolean;
  groupId: string;
  isStoreEnabled: boolean;
  trackDeviceId: boolean;
  suggestMobile: () => Promise<any>;
  isSuggestedMobileMap: boolean;
  getUserHash: () => Promise<any>;
  bucketId: string;
  isPricesRevealedActive: boolean;
  priveBand: string | null;
  isDifferentialPricingEnabled: boolean;
}

const Boot = ({
  configFlagsFetched,
  fetchAuth,
  email,
  firstName,
  formKey,
  id,
  isPro,
  groupId,
  dataFetched,
  userName,
  fetchedOverNetwork,
  storeId,
  isStoreEnabled,
  trackDeviceId,
  suggestMobile,
  isSuggestedMobileMap,
  getUserHash,
  bucketId,
  isPricesRevealedActive,
  priveBand,
  isDifferentialPricingEnabled,
}: BootProps) => {
  const [showIframe, setShowIframe] = useState(false);
  const isZendeskEnabled = useSelector(
    (state: State) => state.remoteConfig.switches.zendeskChatEnabled || false
  );

  const { isSmartLockActive } =
    useSelector((state: State) => state.remoteConfig.configs.configFlags) || {};
  const [iframeUrl, setIframeUrl] = useState('');
  // const isTagsV2Enabled = useIsTagsV2Enabled();
  useEffect(() => {
    if (isStoreEnabled) {
      handleStoreId(storeId);
    }
  }, [storeId, isStoreEnabled]);

  useEffect(() => {
    setUpSessionCount();
  }, []);

  useEffect(() => {
    window.requestIdleCallback(() => {
      fetchAuth();
    });
  }, [fetchAuth]);

  useEffect(() => {
    if (dataFetched && formKey) {
      RequestConfig.setFormKey(formKey);
    }
  }, [dataFetched, formKey]);

  useEffect(() => {
    if (dataFetched && isDifferentialPricingEnabled && id) {
      const isLoggedIn = !!email;
      if (isLoggedIn) {
        setCookie(PRIVE_TIER_KEY, priveBand, 1);
      } else {
        deleteCookie(PRIVE_TIER_KEY);
      }
    }
  }, [dataFetched, email, id, isDifferentialPricingEnabled, priveBand]);

  useEffect(() => {
    const userData = {
      email,
      firstName,
      id,
      isPro,
      groupId,
      priveBand,
    };
    if (dataFetched) {
      trackUser(userData);
      if (trackDeviceId) {
        handleDevice();
      }
    }
  }, [
    email,
    firstName,
    id,
    dataFetched,
    userName,
    fetchedOverNetwork,
    trackDeviceId,
    groupId,
    isPro,
    priveBand,
  ]);

  const handleAutoSignIn = useCallback(() => {
    pushData({ phfill: getMobileNumberLocal() ? 1 : 0 });
    setLoginLocation(LOGIN_LOCATION.MYACCOUNT_LOGIN_BTN);
    if (isSmartLockActive) {
      if (!email) {
        addAutoSignInEvent();
      } else if (fetchedOverNetwork) {
        storeCredentials({ email, userName });
      }
    }
  }, [email, fetchedOverNetwork, userName, isSmartLockActive]);

  const suggestMobileNumber = useCallback(() => {
    suggestMobile().then((res) => {
      const authMapSteps = getAuthSteps();
      deleteAuthSteps();
      if (res?.isEligible) {
        let action = trackingEvent.CTA_SUGGESTED_EMAIL;
        if (authMapSteps) {
          if (authMapSteps == LOGIN_CODES.EMAIL_PASSWORD) {
            action = trackingEvent.CTA_EMAIL_PASS_SOCIAL;
          }
          if (authMapSteps === LOGIN_CODES.EMAIL_OTP) {
            action = trackingEvent.CTA_EMAIL_OTP;
          } else if (authMapSteps > 1) {
            action = trackingEvent.CTA_SOCIAL;
          }
        }
        pushMobileVerificationEvents({ actionName: action, data: res });
        const encodedUrl = encodeURIComponent(
          `${window.location.pathname}${window.location.search}`
        );
        const url = `${MOBILE_VERIFICATION_URL}?redirect=${encodedUrl}`;
        setTimeout(() => {
          setShowIframe(true);
          setIframeUrl(url);
        }, 500);
      }
    });
  }, [suggestMobile]);

  useEffect(() => {
    if (dataFetched && configFlagsFetched) {
      handleAutoSignIn();
    }
  }, [configFlagsFetched, dataFetched, handleAutoSignIn]);

  useEffect(() => {
    if (!dataFetched) {
      setShowIframe(false);
      return;
    }
    const authMapSteps = getAuthSteps();
    if (authMapSteps === LOGIN_CODES.GOOGLE) {
      pushData({ googleSuccess: true });
    }
    if (
      isSuggestedMobileMap &&
      dataFetched &&
      authMapSteps &&
      email &&
      window.location?.pathname?.indexOf(MOBILE_VERIFICATION_URL) < 0
    ) {
      suggestMobileNumber();
    } else {
      deleteAuthSteps();
    }
    // 1 suggested email flow
    // 2 login source google
    // 3 facebook
  }, [dataFetched, suggestMobileNumber, isSuggestedMobileMap, email]);

  useEffect(() => {
    if (isPricesRevealedActive && bucketId) {
      pushData({ bucketId });
    }
  }, [bucketId, isPricesRevealedActive]);

  useEffect(() => {
    if (!dataFetched) {
      return;
    }
    if (!isZendeskEnabled) {
      if (id) {
        getUserHash();
        return;
      }
      loadVerloop();
    }
  }, [dataFetched, getUserHash, id, isZendeskEnabled]);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch({ type: appEvents.SET_PATHNAME });
  });

  // useEffect(() => {
  //   pushData({ isTagsV2Enabled });
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  if (showIframe && dataFetched) {
    return (
      <FrameWrapper id="mobmapping_frame">
        <Iframe
          src={iframeUrl}
          allowFullScreen
        />
      </FrameWrapper>
    );
  }
  return <></>;
};

export default Boot;
